import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  FormHelperText,
  CircularProgress,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import TextFieldUploadButton from "../Buttons/TextFieldUploadButton";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import { homeworkSubmissionTypes } from "../../constants/index";
import { useDispatch, useSelector } from "react-redux";
import { uploadFile } from "../../services/fileUploadService";
import {
  createHomeworkSubmission,
  fetchHomeworkSubmissions
} from "../../redux/actions/learningCenter/contentableActions";
import useLoadingState from "../../hooks/useLoadingState";
import { enqueueErrorSnackbar } from "../../redux/actions/snackbarActions";
import BulletedList from "../BulletedList/BulletedList";
import { makeGetEntities } from "../../redux/selectors/selectors";
import Tooltip from "../Tooltip/Tooltip";

const selector = makeGetEntities();

const getAllowedFileFormatsLabel = fileFormats => {
  return fileFormats && fileFormats.length > 0
    ? fileFormats
        .map(fileFormat => `.${fileFormat.extension.toLowerCase()}`)
        .join(", ")
    : "";
};

const getAllowedFileFormats = (restrictFileFormat, fileFormats) => {
  return restrictFileFormat && fileFormats && fileFormats.length > 0
    ? fileFormats.map(fileFormat => fileFormat.mime).join(",")
    : "*";
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    padding: theme.spacing(7),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4)
    }
  },
  attachmentLink: {
    fontWeight: 500
  }
}));

const SubmitHomeworkButton = ({ isPreviewMode, onSubmit, disabled }) => {
  const { t } = useTranslation();
  const submitBtn = (
    <Button
      variant="contained"
      color="primary"
      disabled={disabled}
      onClick={() => onSubmit()}
      data-qa="course-homework-submit-btn"
    >
      {t("courseHomework.submitBtn")}
    </Button>
  );
  return isPreviewMode ? (
    <Tooltip title={t("coursePreview.notAvailableCoursePreviewMsg")}>
      <div>{submitBtn}</div>
    </Tooltip>
  ) : (
    submitBtn
  );
};
const HomeworkAttachments = ({ attachments }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return attachments && attachments.length > 0 ? (
    <Box display="flex" flexDirection="column" mt={5}>
      <Typography variant="h4" color="textSecondary" sx={{ mb: 3 }}>
        {t("courseHomework.attachments")}
      </Typography>
      <BulletedList
        items={attachments.map((attachment, index) => ({
          itemName: (
            <a
              key={index}
              href={attachment.url}
              className={classes.attachmentLink}
              download={attachment.name}
            >
              {attachment.name}
            </a>
          )
        }))}
      />
    </Box>
  ) : null;
};

const CourseHomework = ({
  homework,
  contentableId,
  userAssignmentId,
  isPreviewMode,
  skeletonLoader
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submissionText, setSubmissionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const isCreatingHomework = useLoadingState("CREATE_HOMEWORK_SUBMISSION");
  const isFetchingHomeworkSubmission = useLoadingState(
    "FETCH_HOMEWORK_SUBMISSIONS"
  );

  // find the homework submission where submitted is true
  const homeworkSubmission = useSelector(
    ({ learningCenter: { contentable } }) => {
      if (homework) {
        const homeworkContentable = selector(contentable, {
          type: "homeworkAssignment",
          id: homework.id
        });
        return (
          homeworkContentable &&
          homeworkContentable.homeworkSubmissions &&
          homeworkContentable.homeworkSubmissions.find(
            ({ submitted }) => submitted
          )
        );
      }
    }
  );
  useEffect(() => {
    setIsSubmitting(isCreatingHomework);
  }, [isCreatingHomework]);

  useEffect(() => {
    if (contentableId && userAssignmentId) {
      dispatch(fetchHomeworkSubmissions(contentableId, userAssignmentId));
    }
  }, [contentableId]);

  useEffect(() => {
    setUploadedFile(null);
    setSubmissionText(null);
  }, [contentableId]);

  const handleSubmit = () => {
    if (!uploadedFile) {
      setError(t("validationMessages.fileSubmissionIsRequired"));
    } else {
      setIsSubmitting(true);
      uploadFile(uploadedFile)
        .then(response => {
          return dispatch(
            createHomeworkSubmission(
              {
                submissionText,
                submissionFileName: response.fileName,
                submissionFileId: response.fileId,
                relationshipNames: ["contentable"],
                contentable: {
                  id: contentableId,
                  type: "contentable"
                }
              },
              t("courseHomework.successMsg"),
              contentableId,
              userAssignmentId
            )
          );
        })
        .then(() => {
          setUploadedFile(null);
          setSubmissionText(null);
        });
    }
  };

  const handleFileUpload = file => {
    const isInvalidMimeType =
      file &&
      homework.restrictFileFormat &&
      !homework.fileFormats.find(
        fileFormat => fileFormat.mime.indexOf(file.type) !== -1
      );
    if (isInvalidMimeType) {
      const errorMsg = t("courseHomework.errorMsg", {
        fileFormats: getAllowedFileFormatsLabel(homework.fileFormats)
      });
      dispatch(
        enqueueErrorSnackbar({
          message: errorMsg
        })
      );
    } else {
      setUploadedFile(file);
    }
    setError(null);
  };

  // Disable all fields if the homework is currently being submitted or there is already a submission that exists
  const disabled =
    isSubmitting || (!!homeworkSubmission && homeworkSubmission.submitted);

  if (isFetchingHomeworkSubmission) {
    return skeletonLoader;
  }

  return homework ? (
    <Paper className={classes.root} data-qa="content-viewer-course-homework">
      <Typography variant="h4" color="textSecondary" sx={{ mb: 3 }}>
        {t("courseHomework.instructions")}
      </Typography>
      <div
        data-qa="course-homework-instructions"
        dangerouslySetInnerHTML={{
          __html: homework.instructions
        }}
      />
      <HomeworkAttachments
        attachments={homework.homeworkAssignmentAttachments}
      />
      <Typography variant="h4" color="textSecondary" sx={{ mt: 5, mb: 4 }}>
        {t("courseHomework.submissions")}
      </Typography>
      <Box width={{ xs: "100%", md: "50%" }} mb={4} minWidth={246}>
        {!homeworkSubmission || !homeworkSubmission.submitted ? (
          <TextFieldUploadButton
            acceptType={getAllowedFileFormats(
              homework.restrictFileFormat,
              homework.fileFormats
            )}
            disableUnderline={false}
            onFileUpload={handleFileUpload}
            file={uploadedFile}
            disabled={disabled}
            helperText={
              homework.restrictFileFormat &&
              `${t(
                "courseHomework.allowedFileFormats"
              )}: ${getAllowedFileFormatsLabel(homework.fileFormats)}`
            }
          />
        ) : (
          <Box ml={-2}>
            <Button
              color="primary"
              onClick={() => window.open(homeworkSubmission.submissionFilePath)}
            >
              <Typography variant="body1">
                {homeworkSubmission.submissionFileName}
              </Typography>
            </Button>
          </Box>
        )}
        {!!error && (
          <Box ml={2} mt={2}>
            <FormHelperText data-qa="course-homework-error-msg" error>
              {error}
            </FormHelperText>
          </Box>
        )}
      </Box>
      {homework.submissionType === homeworkSubmissionTypes.fileAndText && (
        <HTMLEditor
          disabled={isSubmitting}
          readOnly={!!homeworkSubmission && homeworkSubmission.submitted}
          height={250}
          disableMargin
          onChange={newText => setSubmissionText(newText)}
          value={
            !!homeworkSubmission && homeworkSubmission.submitted
              ? homeworkSubmission.submissionText
              : submissionText
          }
          menubar={false}
          toolbar="undo redo | bold italic underline | alignment numlist bullist outdent indent | link"
        />
      )}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mt={4}>
        {isSubmitting && (
          <Box pr={2}>
            <CircularProgress color="primary" size={24} />
          </Box>
        )}
        <SubmitHomeworkButton
          isPreviewMode={isPreviewMode}
          onSubmit={handleSubmit}
          disabled={disabled || isPreviewMode}
        />
      </Box>
    </Paper>
  ) : null;
};

CourseHomework.propTypes = {
  homework: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subscribed: PropTypes.bool,
    type: PropTypes.string,
    title: PropTypes.string,
    submissionType: PropTypes.string,
    restrictFileFormat: PropTypes.bool,
    instructions: PropTypes.string,
    fileFormats: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        mime: PropTypes.string,
        category: PropTypes.string,
        extension: PropTypes.string
      })
    )
  }),
  // The ID for the course contentable that the homework submission is associated to
  contentableId: PropTypes.string,
  // true if the course is in preview mode, false otherwise.
  isPreviewMode: PropTypes.bool
};

export default CourseHomework;
